import React, { useEffect, useState, useCallback, useRef } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { googleMapsApiKey, mapsLibraries } from "../../../env";
import { getDisplayType } from "../../../utils/helper";
import { buildStyles } from "./components/styles";
import LandscapeEta from "./components/LandscapeEta";
import PortraitEta from "./components/PortraitEta";
import SquareEta from "./components/SquareEta";
import FooterEta from "./components/FooterEta";

const ROUTE_COLORS = ["#6C4DF6", "#e11d48", "#0891b2", "#f59e0b"];

const SPEED_COLORS = {
  SLOW: "#f9ab00",
  TRAFFIC_JAM: "#d93025",
};

// Chromium 63-safe helpers (no ?. / ??)
const get = (obj, path, fallback) => {
  if (fallback === undefined) fallback = undefined;
  var cur = obj;
  for (var i = 0; i < path.length; i++) {
    if (cur == null) return fallback;
    cur = cur[path[i]];
  }
  return cur === undefined || cur === null ? fallback : cur;
};

const fetchTrafficRoute = async (origin, dest) => {
  try {
    const resp = await fetch(
      "https://routes.googleapis.com/directions/v2:computeRoutes",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": googleMapsApiKey,
          "X-Goog-FieldMask":
            "routes.polyline.encodedPolyline,routes.travelAdvisory.speedReadingIntervals",
        },
        body: JSON.stringify({
          origin: {
            location: {
              latLng: { latitude: origin.lat, longitude: origin.lng },
            },
          },
          destination: {
            location: {
              latLng: { latitude: dest.lat, longitude: dest.lng },
            },
          },
          travelMode: "DRIVE",
          routingPreference: "TRAFFIC_AWARE",
          // primary route only — alt routes paint yellow/red off the A→B path
          computeAlternativeRoutes: false,
          extraComputations: ["TRAFFIC_ON_POLYLINE"],
          polylineQuality: "HIGH_QUALITY",
        }),
      }
    );
    if (!resp.ok) return null;
    const data = await resp.json();
    const geom = get(window, ["google", "maps", "geometry"]);
    if (!data.routes || !data.routes.length || !geom) return null;

    // traffic ONLY on the primary route polyline
    var route = data.routes[0];
    const encoded = get(route, ["polyline", "encodedPolyline"]);
    const intervals = get(route, ["travelAdvisory", "speedReadingIntervals"]);
    if (!encoded || !intervals || !intervals.length) return null;

    const pts = geom.encoding.decodePath(encoded).map(function (p) {
      return { lat: p.lat(), lng: p.lng() };
    });

    const segments = [];
    intervals.forEach(function (iv) {
      const color = SPEED_COLORS[iv.speed];
      if (!color) return;
      const start = iv.startPolylinePointIndex || 0;
      const end = iv.endPolylinePointIndex || 0;
      const path = pts.slice(start, end + 1);
      if (path.length > 1) segments.push({ path: path, color: color });
    });

    return { path: pts, segments: segments };
  } catch (e) {
    // no traffic overlay is fine — the plain route still draws
    return null;
  }
};

// ~20–25m (degrees²) — keep overlays glued to drawn route
const NEAR_THRESH = 0.00022 * 0.00022;

const pointLatLng = function (p) {
  if (!p) return null;
  if (typeof p.lat === "function") return { lat: p.lat(), lng: p.lng() };
  return { lat: p.lat, lng: p.lng };
};

const nearPath = function (lat, lng, path) {
  for (var i = 0; i < path.length; i++) {
    var q = pointLatLng(path[i]);
    if (!q) continue;
    var dLat = lat - q.lat;
    var dLng = lng - q.lng;
    if (dLat * dLat + dLng * dLng < NEAR_THRESH) return true;
  }
  return false;
};

// keep segment only if most samples sit on the main A→B path
const segmentOnRoute = function (segPath, routePath) {
  if (!segPath || !segPath.length || !routePath || !routePath.length)
    return false;
  var step = Math.max(1, Math.floor(segPath.length / 10));
  var hits = 0;
  var samples = 0;
  for (var i = 0; i < segPath.length; i += step) {
    samples++;
    var p = pointLatLng(segPath[i]);
    if (p && nearPath(p.lat, p.lng, routePath)) hits++;
  }
  var last = pointLatLng(segPath[segPath.length - 1]);
  if (last) {
    samples++;
    if (nearPath(last.lat, last.lng, routePath)) hits++;
  }
  return samples > 0 && hits / samples >= 0.7;
};

const mostDivergentPoint = function (paths, i) {
  var self = paths[i] || [];
  if (!self.length) return null;
  var others = [];
  paths.forEach(function (p, j) {
    if (j !== i) {
      for (var k = 0; k < p.length; k++) others.push(p[k]);
    }
  });
  if (!others.length) return self[Math.floor(self.length / 2)];

  var best = self[0];
  var bestMin = -1;
  for (var si = 0; si < self.length; si++) {
    var p = self[si];
    var minD = Infinity;
    for (var oi = 0; oi < others.length; oi++) {
      var q = others[oi];
      var dLat = p.lat() - q.lat();
      var dLng = p.lng() - q.lng();
      var d = dLat * dLat + dLng * dLng;
      if (d < minD) {
        minD = d;
        if (minD === 0) break;
      }
    }
    if (minD > bestMin) {
      bestMin = minD;
      best = p;
    }
  }
  return best;
};

const trafficLevel = function (trafficSec, freeSec) {
  // Only skip the pill when we truly have no traffic data. Otherwise always
  // show a status: Light (free-flowing) / Moderate / Heavy, based on how much
  // the traffic-aware time exceeds the free-flow time.
  if (!freeSec || !trafficSec) return null;
  var ratio = trafficSec / freeSec;
  // `color` is for text on white (kept dark enough to stay legible); `fill` is
  // the brighter card background. Light traffic needs the lift most — a
  // text-weight green would disappear into the green panel behind the card.
  if (ratio <= 1.15)
    return { label: "Light Traffic", color: "#15803d", fill: "#17a34a" };
  if (ratio <= 1.4)
    return { label: "Moderate Traffic", color: "#b45309", fill: "#c2610a" };
  return { label: "Heavy Traffic", color: "#b91c1c", fill: "#c62828" };
};

const legSecs = function (leg) {
  if (!leg) return Infinity;
  var d =
    (leg.duration_in_traffic && leg.duration_in_traffic.value) ||
    (leg.duration && leg.duration.value);
  return d != null ? d : Infinity;
};

const legText = function (leg) {
  if (!leg) return "";
  var d = leg.duration_in_traffic || leg.duration;
  return (d && d.text) || "";
};

const parseSecs = function (val) {
  if (val == null) return null;
  var n = parseInt(String(val).replace("s", ""), 10);
  return isNaN(n) ? null : n;
};

const formatDuration = function (secs) {
  if (secs == null) return "";
  var mins = Math.round(secs / 60);
  if (mins < 1) mins = 1;
  if (mins < 60) return mins + " min" + (mins === 1 ? "" : "s");
  var hrs = Math.floor(mins / 60);
  var rem = mins % 60;
  var out = hrs + " hour" + (hrs === 1 ? "" : "s");
  if (rem) out += " " + rem + " min" + (rem === 1 ? "" : "s");
  return out;
};

const formatDistance = function (meters) {
  if (meters == null) return "";
  if (meters < 1000) return Math.round(meters) + " M";
  return (meters / 1000).toFixed(1) + " KM";
};

const buildLabels = function (d) {
  var addr = (d.location && d.location.address) || "";
  return {
    label: d.customName || addr.split(",")[0].trim(),
    fullLabel: d.customName || addr.split(",").slice(0, 3).join(",").trim(),
  };
};

const LAYOUTS = {
  landscape: LandscapeEta,
  portrait: PortraitEta,
  square: SquareEta,
  footer: FooterEta,
  "mini-footer": FooterEta,
};

const LiveEtaBody = function (props) {
  var widget = props.widget;
  var isLoaded = props.isLoaded;
  var loadError = props.loadError;

  var data = (widget && widget.data) || {};
  var pickup = data.pickup;
  var destinations =
    data.destinations ||
    (data.destination ? [data.destination] : []);

  var mapEnabled = data.showMap !== false;
  var mapsApiReady =
    isLoaded &&
    !loadError &&
    !!(window.google && window.google.maps && window.google.maps.Map);
  var showMap = mapEnabled && mapsApiReady;

  var layout = getDisplayType(window.innerWidth, window.innerHeight);

  // The map-hidden layouts have their own sizing scale (the nm* styles), so
  // the old blanket bump of the map-mode panel styles is no longer needed.
  var contentScale = 1;

  var mapRef = useRef(null);
  var mapDataRef = useRef(false);
  // Last successfully fetched routes. On a failed refresh (e.g. offline) we
  // keep showing these instead of wiping the ETA to an error.
  var routesRef = useRef([]);
  var _st = useState([]);
  var routes = _st[0];
  var setRoutes = _st[1];
  var _er = useState("");
  var error = _er[0];
  var setError = _er[1];

  var validDest = destinations.filter(function (d) {
    return d && d.location && d.location.lat != null;
  });

  var s = buildStyles(
    window.innerWidth,
    window.innerHeight,
    validDest.length || 1,
    contentScale
  );

  var destKey = validDest
    .map(function (d) {
      return d.location.lat + "," + d.location.lng;
    })
    .join("|");

  var fetchRoutes = useCallback(
    function () {
      if (
        !window.google ||
        !pickup ||
        !pickup.location ||
        pickup.location.lat == null ||
        !validDest.length
      )
        return;

      var isSingle = validDest.length === 1;
      var service = new window.google.maps.DirectionsService();
      Promise.all(
        validDest.map(function (d, i) {
          return new Promise(function (resolve) {
            service.route(
              {
                origin: {
                  lat: pickup.location.lat,
                  lng: pickup.location.lng,
                },
                destination: {
                  lat: d.location.lat,
                  lng: d.location.lng,
                },
                travelMode: window.google.maps.TravelMode.DRIVING,
                provideRouteAlternatives: isSingle,
                drivingOptions: {
                  departureTime: new Date(),
                  trafficModel: "bestguess",
                },
              },
              function (res, status) {
                // a failed leg resolves null; the caller keeps the last good ETA
                if (status !== "OK") return resolve(null);

                var routeSecs = function (rt) {
                  return legSecs(rt.legs && rt.legs[0]);
                };
                var fastestIdx = 0;
                res.routes.forEach(function (rt, idx) {
                  if (routeSecs(rt) < routeSecs(res.routes[fastestIdx]))
                    fastestIdx = idx;
                });

                var allPaths = res.routes.map(function (rt) {
                  return rt.overview_path || [];
                });
                var alts = isSingle
                  ? res.routes.map(function (rt, idx) {
                      var l = rt.legs[0];
                      var path = allPaths[idx];
                      var mid = mostDivergentPoint(allPaths, idx);
                      var center =
                        path && path.length
                          ? path[Math.floor(path.length / 2)]
                          : null;
                      var anchor =
                        idx === fastestIdx ? center || mid : mid;
                      return {
                        routeIndex: idx,
                        isFastest: idx === fastestIdx,
                        duration: legText(l),
                        distance: (
                          (l.distance && l.distance.text) ||
                          ""
                        ).toUpperCase(),
                        path: path,
                        lat: anchor ? anchor.lat() : d.location.lat,
                        lng: anchor ? anchor.lng() : d.location.lng,
                      };
                    })
                  : [];

                var leg = res.routes[fastestIdx].legs[0];
                var addr =
                  (d.location && d.location.address) || "";
                var base = {
                  index: i,
                  label:
                    d.customName ||
                    addr.split(",")[0].trim(),
                  fullLabel:
                    d.customName ||
                    addr
                      .split(",")
                      .slice(0, 3)
                      .join(",")
                      .trim(),
                  directions: res,
                  routeIndex: fastestIdx,
                  alts: alts,
                  color: ROUTE_COLORS[i % ROUTE_COLORS.length],
                  distance: (
                    (leg.distance && leg.distance.text) ||
                    ""
                  ).toUpperCase(),
                  duration: legText(leg),
                  durationSecs: (function () {
                    var sec = legSecs(leg);
                    return isFinite(sec) ? sec : null;
                  })(),
                  traffic: trafficLevel(
                    leg.duration_in_traffic &&
                      leg.duration_in_traffic.value,
                    leg.duration && leg.duration.value
                  ),
                };

                fetchTrafficRoute(pickup.location, d.location).then(
                  function (trafficRoute) {
                    // only keep congestion on the selected (fastest) path
                    var mainPath = allPaths[fastestIdx] || [];
                    if (
                      trafficRoute &&
                      trafficRoute.segments &&
                      trafficRoute.segments.length
                    ) {
                      trafficRoute.segments = trafficRoute.segments.filter(
                        function (seg) {
                          return segmentOnRoute(seg.path, mainPath);
                        }
                      );
                    }
                    resolve(
                      Object.assign({}, base, { trafficRoute: trafficRoute })
                    );
                  }
                );
              }
            );
          });
        })
      ).then(function (results) {
        var ok = results.filter(Boolean);
        if (ok.length) {
          setError("");
          mapDataRef.current = true;
          setRoutes(ok);
          routesRef.current = ok;
        } else if (!routesRef.current.length) {
          // Nothing fetched and no previous ETA to fall back on.
          setError("Unable to fetch routes");
        }
        // else: fetch failed (e.g. offline) but we already have a last-good
        // ETA — keep it on screen instead of wiping to an error.
      });
    },
    [pickup, destKey]
  );

  var fetchEtaOnly = useCallback(
    function () {
      if (
        ! pickup ||
        !pickup.location ||
        pickup.location.lat == null ||
        !validDest.length
      )
        return;

      Promise.all(
        validDest.map(function (d, i) {
          return fetch(
            "https://routes.googleapis.com/directions/v2:computeRoutes",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Goog-Api-Key": googleMapsApiKey,
                "X-Goog-FieldMask":
                  "routes.duration,routes.staticDuration,routes.distanceMeters",
              },
              body: JSON.stringify({
                origin: {
                  location: {
                    latLng: {
                      latitude: pickup.location.lat,
                      longitude: pickup.location.lng,
                    },
                  },
                },
                destination: {
                  location: {
                    latLng: {
                      latitude: d.location.lat,
                      longitude: d.location.lng,
                    },
                  },
                },
                travelMode: "DRIVE",
                routingPreference: "TRAFFIC_AWARE",
                computeAlternativeRoutes: false,
              }),
            }
          )
            .then(function (resp) {
              return resp.ok ? resp.json() : null;
            })
            .then(function (data) {
              if (!data || !data.routes || !data.routes.length) return null;
              var rt = data.routes[0];
              var trafficSec = parseSecs(rt.duration);
              var freeSec = parseSecs(rt.staticDuration);
              var labels = buildLabels(d);
              return {
                index: i,
                label: labels.label,
                fullLabel: labels.fullLabel,
                color: ROUTE_COLORS[i % ROUTE_COLORS.length],
                distance: formatDistance(rt.distanceMeters),
                duration: formatDuration(
                  trafficSec != null ? trafficSec : freeSec
                ),
                durationSecs: trafficSec != null ? trafficSec : freeSec,
                traffic: trafficLevel(trafficSec, freeSec),
                directions: null,
                routeIndex: 0,
                alts: [],
                trafficRoute: null,
              };
            })
            .catch(function (e) {
              // offline or rate-limited — caller keeps the last good ETA
              return null;
            });
        })
      ).then(function (results) {
        if (mapDataRef.current) return;
        var ok = results.filter(Boolean);
        if (ok.length) {
          setError("");
          setRoutes(ok);
          routesRef.current = ok;
        } else if (!routesRef.current.length) {
          setError("Unable to fetch routes");
        }
        // else: keep the last good ETA on a failed refresh (e.g. offline).
      });
    },
    [pickup, destKey]
  );

  useEffect(
    function () {
      var interval = null;

      var hasMapsJs = function () {
        return !!(
          window.google &&
          window.google.maps &&
          window.google.maps.DirectionsService
        );
      };

      if (mapEnabled && hasMapsJs()) {
        fetchRoutes();
        interval = setInterval(fetchRoutes, 120000);
      } else {
        fetchEtaOnly();
        interval = setInterval(fetchEtaOnly, 120000);
      }

      return function () {
        if (interval) clearInterval(interval);
      };
    },
    [mapEnabled, isLoaded, loadError, fetchRoutes, fetchEtaOnly]
  );

  useEffect(
    function () {
      if (
        !mapRef.current ||
        !window.google ||
        !pickup ||
        !pickup.location ||
        pickup.location.lat == null
      )
        return;
      var bounds = new window.google.maps.LatLngBounds();
      bounds.extend({
        lat: pickup.location.lat,
        lng: pickup.location.lng,
      });
      validDest.forEach(function (d) {
        bounds.extend({ lat: d.location.lat, lng: d.location.lng });
      });

      if (s.is98) {
        routes.forEach(function (r) {
          var dirs = r.directions && r.directions.routes;
          if (!dirs) return;
          dirs.forEach(function (rt) {
            if (rt.bounds) bounds.union(rt.bounds);
          });
        });
        var div = mapRef.current.getDiv();
        var mw = (div && div.offsetWidth) || window.innerWidth;
        var mh = (div && div.offsetHeight) || window.innerHeight;
        mapRef.current.fitBounds(bounds, {
          top: Math.round(mh * 0.14),
          bottom: Math.round(mh * 0.1),
          left: Math.round(mw * 0.1),
          right: Math.round(mw * 0.1),
        });
      } else {
        mapRef.current.fitBounds(bounds, 24);
      }

      if (layout === "portrait") {
        var map = mapRef.current;
        window.google.maps.event.addListenerOnce(map, "idle", function () {
          map.setZoom(map.getZoom() + 0.14);
        });
      }
    },
    [routes]
  );


  if (
    !pickup ||
    !pickup.location ||
    pickup.location.lat == null ||
    !validDest.length
  ) {
    return (
      <div style={s.center}>
        <p>Pickup and destination are not configured</p>
      </div>
    );
  }

  var Layout = LAYOUTS[layout] || LandscapeEta;

  return (
    <Layout
      s={s}
      pickup={pickup}
      routes={routes}
      error={error}
      single={validDest.length === 1}
      showMap={showMap}
      onMapLoad={function (m) {
        mapRef.current = m;
      }}
    />
  );
};

const WithMaps = function (props) {
  var loader = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: googleMapsApiKey,
    libraries: mapsLibraries,
  });
  return (
    <LiveEtaBody
      widget={props.widget}
      isLoaded={loader.isLoaded}
      loadError={loader.loadError}
    />
  );
};

const WithoutMaps = function (props) {
  return (
    <LiveEtaBody widget={props.widget} isLoaded={false} loadError={null} />
  );
};

const Index = function (props) {
  var widget = props.widget;
  var data = (widget && widget.data) || {};
  var mapEnabled = data.showMap !== false;
  return mapEnabled ? (
    <WithMaps widget={widget} />
  ) : (
    <WithoutMaps widget={widget} />
  );
};

export default Index;
